// ============================================================================
//  biometric.api.ts — Couche API Module Biométrie (terminal ZKTeco iFace3)
//  Miroir du backend (commits 87737d1 / f08ce49). Import manuel *_attlog.dat,
//  association employé ↔ zkUserId, recalcul des journées.
// ============================================================================

import api from '@/lib/axios'

// ─── Types (miroir backend) ─────────────────────────────────────────────────

export interface BiometricPeriod {
  firstPunchAt: string | null
  lastPunchAt:  string | null
}

export interface RecomputeResult {
  employeesProcessed:  number
  daysComputed:        number
  attendancesUpserted: number
  skippedManual:       number
}

/** Réponse de POST /biometric/imports. */
export interface BiometricImportResult {
  importId:            string
  deviceSn:            string
  fileName:            string
  linesRead:           number
  linesNew:            number
  droppedUnknownUser:  number
  malformed:           number
  malformedSamples:    string[]
  period:              BiometricPeriod
  distinctZkUserIdCount: number
  distinctZkUserIds:   string[]
  unknownZkUserIds:    string[]
  clockDriftMinutes:   number | null
  driftAlert:          boolean
  driftMessage:        string | null
  recompute:           RecomputeResult | null
}

/** Élément d'historique des imports. */
export interface BiometricImport {
  id:                string
  entityId:          string
  fileName:          string
  deviceSn:          string
  importedById:      string
  importedAt:        string
  linesRead:         number
  linesNew:          number
  firstPunchAt:      string | null
  lastPunchAt:       string | null
  clockDriftMinutes: number | null
}

/** Terminal ZKTeco déclaré (nom lisible pour le n° de série). */
export interface BiometricDevice {
  id:           string
  entityId:     string
  deviceSn:     string
  name:         string
  location:     string | null
  isActive:     boolean
  lastImportAt: string | null
  createdAt:    string
  updatedAt:    string
}

/** Couple (terminal, numéro) présent dans les pointages mais sans employé associé. */
export interface UnmatchedPunch {
  deviceSn:    string
  zkUserId:    string
  punchCount:  number
  lastPunchAt: string | null
}

// ─── API ────────────────────────────────────────────────────────────────────

export const biometricApi = {
  // Import d'un fichier <SN>_attlog.dat (multipart). Refuse les gabarits côté backend.
  importAttlog: (file: File): Promise<BiometricImportResult> => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post('/biometric/imports', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => r.data.data ?? r.data)
  },

  // Historique des imports (plus récent en premier)
  listImports: (): Promise<BiometricImport[]> =>
    api.get('/biometric/imports').then(r => r.data.data ?? r.data),

  // zkUserId sans employé associé
  listUnmatched: (): Promise<UnmatchedPunch[]> =>
    api.get('/biometric/unmatched').then(r => r.data.data ?? r.data),

  // Relance le calcul des journées sur une plage (jours UTC)
  recompute: (dateFrom: string, dateTo: string): Promise<RecomputeResult> =>
    api.post('/biometric/recompute', { dateFrom, dateTo }).then(r => r.data.data ?? r.data),

  // Associe un employé au couple (terminal, numéro)
  assignZkUserId: (employeeId: string, zkDeviceSn: string, zkUserId: string): Promise<any> =>
    api.patch(`/employees/${employeeId}/zk-user-id`, { zkDeviceSn, zkUserId }).then(r => r.data.data ?? r.data),

  // ─── Terminaux ────────────────────────────────────────────────────────────
  listDevices: (): Promise<BiometricDevice[]> =>
    api.get('/biometric/devices').then(r => r.data.data ?? r.data),

  updateDevice: (id: string, dto: { name?: string; location?: string | null; isActive?: boolean }): Promise<BiometricDevice> =>
    api.patch(`/biometric/devices/${id}`, dto).then(r => r.data.data ?? r.data),

  deleteDevice: (id: string): Promise<{ message: string }> =>
    api.delete(`/biometric/devices/${id}`).then(r => r.data.data ?? r.data),
}
